/*
 * -----------------------------------------------------------------------
 *  <copyright file="draggable-row/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { NormalizedDragEvent, useDraggable, useDroppable } from '@progress/kendo-react-common';
import { GridCustomRowProps } from '@progress/kendo-react-grid';
import React from 'react';
import cn from 'clsx';
import { ReorderContext } from '@components/core/grid-helper';

interface DraggableRowProps extends GridCustomRowProps {
    lineHeight?: string;
    scrollAvailable?: number;
}

const DraggableRow = (props: DraggableRowProps) => {
    const { lineHeight, scrollAvailable, children, trProps, onClick, dataItem } = props;

    const [dropped, setDropped] = React.useState(false);
    const [dragged, setDragged] = React.useState(false);
    const [direction, setDirection] = React.useState<'before' | 'after' | null>(null);
    const [initial, setInitial] = React.useState({ x: 0, y: 0 });
    const { dragStart, reorder } = React.useContext(ReorderContext);

    const element = React.useRef<HTMLTableRowElement | null>(null);

    const handlePress = (event: NormalizedDragEvent) => {
        setInitial({
            x: event.clientX - event.offsetX,
            y: event.clientY - event.offsetY,
        });
    };

    const handleDragStart = (event: NormalizedDragEvent) => {
        if (!event.originalEvent.target || !(event.originalEvent.target as HTMLElement).dataset.dragHandle) {
            return;
        }
        setDragged(true);
        dragStart(dataItem);
    };

    const handleDrag = (event: NormalizedDragEvent) => {
        if (!element.current || !dragged) {
            return;
        }
        const scrollMagic = 100; // Magic value of maximum available scroll for elements that don't require container scroll
        element.current.style.transform = `translateY(${
            event.clientY -
            initial.y +
            (scrollAvailable && event.scrollY > scrollMagic ? scrollAvailable : event.scrollY < 0 ? event.scrollY : 0)
        }px)`;
    };

    const handleDragEnd = () => {
        setDragged(false);
        setDropped(false);
        setInitial({ x: 0, y: 0 });
    };

    const handleRelease = () => {
        if (!element.current) {
            return;
        }
        element.current.style.transform = null;
    };

    const handleDragEnter = () => {
        setDropped(true);
        setDirection(null);
    };

    const handleDragOver = (event: NormalizedDragEvent) => {
        if (!element.current) {
            return;
        }
        const rect = element.current.getBoundingClientRect();
        setDirection(rect.top + rect.height / 2 <= event.pageY ? 'after' : 'before');
    };

    const handleDragLeave = () => {
        setDropped(false);
        setDirection(null);
    };

    const handleDrop = () => {
        reorder(dataItem, direction);
        setDropped(false);
        setDirection(null);
    };

    useDraggable(
        element,
        {
            onPress: handlePress,
            onDragStart: handleDragStart,
            onDrag: handleDrag,
            onDragEnd: handleDragEnd,
            onRelease: handleRelease,
        },
        { autoScroll: dragged },
    );

    useDroppable(element, {
        onDragEnter: handleDragEnter,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
    });

    return (
        <>
            {dropped && direction === 'before' && (
                <tr
                    style={{
                        outlineStyle: 'solid',
                        outlineWidth: 1,
                        outlineColor: 'red',
                    }}
                />
            )}
            <tr
                {...trProps}
                onClick={onClick}
                ref={element}
                style={{
                    userSelect: 'none',
                    pointerEvents: dragged ? 'none' : undefined,
                    opacity: dragged ? '0.8' : undefined,
                    lineHeight: lineHeight || null,
                }}
                className={cn(trProps?.className, props?.isAltRow ? 'k-alt' : '')}
            >
                {children}
            </tr>
            {dropped && direction === 'after' && (
                <tr
                    style={{
                        outlineStyle: 'solid',
                        outlineWidth: 1,
                        outlineColor: 'red',
                    }}
                />
            )}
        </>
    );
};

export default DraggableRow;
